import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import type { DocumentReferenceResponse, CreateProjectRequest } from "api/generatedApi";
import DialogTitleStyled from "components/shared/dialog/dialog-title-styled";
import { SubmitHandler, useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormDialogProps } from "shared/dialog/types";
import { NoteField } from "shared/editors/note-field";
import { useScreenSize, ScreenSizeEnum } from "shared/use-screen-size";
import { UploadField } from "shared/editors/upload-field";
import { GetProjectResponse } from "api/generatedApi";
import { Box, Divider, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import ErrorSharpIcon from "@mui/icons-material/ErrorSharp";
import { useProjectRestrictions } from "shared/user-restrictions/use-project-restrictions";
import { useToast } from "shared/toast/hooks/use-toast";
import { useGetApiConfigQuery } from "api/generatedApi";

export interface CreateProjectFormData extends CreateProjectRequest {}

interface Props extends FormDialogProps<CreateProjectFormData> {
  project: GetProjectResponse;
  documents: DocumentReferenceResponse[];
  handleFileChange: (file: File) => Promise<void>; // function to handle file changes
  isFileUploading?: boolean;
  fileOnChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  clickDocumentDelete: (document: DocumentReferenceResponse) => void;
  onClose: () => void;
}

export function DocumentationModal(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  // Only initialize from props.documents when the modal opens
  const [localDocuments, setLocalDocuments] = useState<DocumentReferenceResponse[]>(props.documents);
  const [tempFilesforUpload, setTempFilesforUpload] = useState<File[]>([]);
  const { canEditDescription, canDeleteDocument, canDownloadDocument, canUploadDocument } = useProjectRestrictions(props.project);
  const { data: configData } = useGetApiConfigQuery();
  const [tempDocForDelete, setTempDocForDelete] = useState<DocumentReferenceResponse[]>([]);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const maxProjectDescriptionLength = 1500;

  useEffect(() => {
    setLocalDocuments((prev) => {
      // Add new docs from props.documents not in prev
      const newDocs = props.documents.filter((doc) => !prev.some((localDoc) => localDoc.documentId && localDoc.documentId === doc.documentId));
      // Remove temp docs that now exist in props.documents
      const filteredPrev = prev.filter((localDoc) => !localDoc.documentId?.startsWith("temp-") || !props.documents.some((doc) => doc.name === localDoc.name));
      return [...filteredPrev, ...newDocs];
    });
  }, [props.documents]);

  const {
    control,
    formState: { isValid, isDirty },
    handleSubmit,
  } = useForm<CreateProjectFormData>({
    mode: "all",
    defaultValues: {
      description: props.project?.description || "",
    },
  });

  const { screenSize } = useScreenSize();

  const onSubmit: SubmitHandler<CreateProjectFormData> = async (data) => {
    //upload files
    if (tempFilesforUpload.length > 0) {
      for (const file of tempFilesforUpload) {
        await props.handleFileChange(file);
      }
    }

    //delete files
    if (tempDocForDelete.length > 0) {
      for (const doc of tempDocForDelete) {
        await props.clickDocumentDelete(doc);
      }
    }

    //notefield submit
    await props.onSubmit(data);
  };

  const handleDeleteDocument = (document: DocumentReferenceResponse) => {
    // a temporary document is created with a id starting with "temp-"
    if (!document.documentId?.startsWith("temp-")) {
      setTempDocForDelete((prev) => [...prev, document]);
    }
    // Remove the document from localDocuments
    // This is necessary to update the UI immediately without waiting for the API response
    setLocalDocuments((prev) => prev.filter((doc) => doc.documentId !== document.documentId));

    //remove from tempFilesforUpload if the name matches
    setTempFilesforUpload((prev) => prev.filter((file) => file.name !== document.name));
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // get the file from the event
    const target = event.target;
    const fileObj = target?.files && target.files[0];

    if (fileObj && configData?.maxUploadFileSizeMB && fileObj?.size > configData?.maxUploadFileSizeMB * 1024 * 1024) {
      // If the file is larger than the max upload size, show an error message
      toast.error(t("content.overview.documents.uploadFileSizeError", { maxFileSize: configData?.maxUploadFileSizeMB ?? 25 }));
      return;
    }

    if (fileObj) {
      //get the file name
      const baseName = fileObj.name;
      //find the file extension
      const extMatch = baseName.match(/(\.[^\.]+)$/);
      // If no extension is found, use an empty string
      const ext = extMatch ? extMatch[0] : "";
      // Remove the extension from the base name
      const nameWithoutExt = baseName.replace(/(\.[^\.]+)$/, "");
      // get the base name in a let
      let uniqueName = baseName;
      //number to append the file name with if it exists
      let counter = 0;
      // Check if the file name already exists in localDocuments
      // If it does, use a loop to append a number to the file name until it is unique
      let exists = localDocuments.some((doc) => typeof doc.name === "string" && doc.name === uniqueName);
      while (exists) {
        counter++;
        uniqueName = `${nameWithoutExt} (${counter})${ext}`;
        exists = localDocuments.some((doc) => typeof doc.name === "string" && doc.name === uniqueName);
      }
      // Create a new File object with the unique name but the same content as the original file
      // This is necessary because the File constructor does not allow changing the name of an existing file
      const renamedFile = new File([fileObj], uniqueName, { type: fileObj.type });

      // Update localDocuments with the new file for the visual representation
      setLocalDocuments((prev) => [
        ...prev,
        {
          name: renamedFile.name,
          documentId: `temp-${renamedFile.name}-${Date.now()}`,
          size: renamedFile.size,
          uploadedTimestamp: new Date().toISOString(),
        },
      ]);
      // Add the new file to the tempFilesforUpload state
      setTempFilesforUpload((prev) => [...prev, renamedFile]);
    }

    if (event.target) {
      event.target.value = ""; // allows for re-uploading the same file
    }
  };

  const dirtyCheck = () => tempFilesforUpload.length > 0 || tempDocForDelete.length > 0 || isDirty;

  const handleClose = () => {
    if (dirtyCheck()) {
      // If dirty, show a confirmation dialog before closing
      setConfirmEdit(true);
    } else {
      props.onClose();
    }
  };

  const onSubmitHandler = handleSubmit(onSubmit);

  return (
    <>
      <Dialog
        fullWidth
        open={props.isOpen && !confirmEdit}
        maxWidth="xl"
        PaperProps={{
          sx: {
            height: "auto",
            margin: screenSize === ScreenSizeEnum.Mobile ? "32px 7px" : "32px",
            width: screenSize === ScreenSizeEnum.Mobile ? "100%" : "calc(100% - 64px)",
          },
        }}
      >
        <DialogTitleStyled title={t("dashboard.projectInfo.modal.title")} description={t("dashboard.projectInfo.modal.description")} onClose={handleClose} isOpen={props.isOpen} bottomPadding={0} />
        <DialogContent>
          <Controller
            rules={{ maxLength: maxProjectDescriptionLength }}
            name="description"
            control={control}
            render={({ field }) => (
              <NoteField
                noContentText={t("dashboard.projectInfo.documentation.noContentTextDescription")}
                inADialog={true}
                value={field.value}
                onChange={field.onChange}
                label="Projekt beskrivelse"
                canEditNote={canEditDescription()}
                screenSize={screenSize}
                disableButton={true}
                disableEdit={false}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: "-10px", pt: "-10px", pl: "30px" }}>
            {t("dashboard.projectInfo.documentation.descriptionHint", { maxLength: maxProjectDescriptionLength })}
          </Typography>
          <Box sx={{ width: "90%", mx: "auto" }}>
            <Divider />
          </Box>
          <UploadField
            allowDownloads={canDownloadDocument()}
            isUploading={props.isFileUploading}
            noContentText={t("dashboard.projectInfo.documentation.noContentTextUpload")}
            title="Dokumenter"
            canUploadDocument={canUploadDocument}
            documents={localDocuments}
            handleFileChange={handleFileChange}
            screenSize={useScreenSize().screenSize}
            inADialog={true}
            canDeleteDocument={canDeleteDocument}
            clickDocumentDelete={handleDeleteDocument}
            textColor="primary.black"
          />
        </DialogContent>

        <DialogActions>
          <Button variant="contained" color="primary" disabled={!isValid} onClick={onSubmitHandler}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="xs"
        fullWidth
        open={confirmEdit}
        onClose={() => setConfirmEdit(false)}
        PaperProps={{
          sx: {
            height: "auto",
            margin: screenSize === ScreenSizeEnum.Mobile ? "32px 7px" : "32px",
            width: screenSize === ScreenSizeEnum.Mobile ? "100%" : "calc(100% - 64px)",
          },
        }}
      >
        <DialogContent sx={{ display: "flex", textAlign: "center" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <ErrorSharpIcon sx={{ color: "primary.dark", alignSelf: "center" }} fontSize="large"></ErrorSharpIcon>

            <Typography variant="h4" color="primary.dark" textAlign={"center"}>
              {t("content.modal.confirmCancelTitle")}
            </Typography>
            <Typography variant="body1" color="grey.50" textAlign={"center"}>
              {t("content.modal.confirmCancelDescription")}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setConfirmEdit(false);
            }}
          >
            {t("common.back")}
          </Button>
          <Button variant="contained" color="primary" onClick={props.onClose}>
            {t("common.finish")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
